import React from 'react'
import { useSelector } from '../../../../Flux/index.js'
import { selectSourceCubeRefs } from '../../../../Flux/Slice/Visualization/Selectors.js'
import { getCubeTable } from '../../../../Repository/Visualization/CubeTableRegistry.js'
import { ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline'

/**
 * Scope-wide banner shown above the sinks grid. Distinguishes the three "nothing renders" cases
 * that otherwise all look like a generic spinner / empty chart:
 *
 *   - **error**: at least one cube failed to load.
 *   - **no precomputed data**: every cube the sources need has loaded but is empty (0 rows).
 *     This is the "opened an order/project that has rows but no *derived* data yet" case — the
 *     backend returns an empty Arrow stream (and a `null` ETag) for an un-refreshed scope.
 *
 * When cubes are still loading, or any cube has data, the banner renders nothing and the normal
 * per-sink loading / chart UI takes over.
 *
 * The row counts come from the side `CubeTableRegistry` (not Redux); this component re-renders
 * whenever the cube *statuses* change, and the loader registers the decoded table before
 * flipping a cube to `'ready'`, so the count is available by the time we read it.
 */
export default function WorkspaceDataNotice({ scopeKind }: { scopeKind?: 'project' | 'order' }) {
  const refs = useSelector(selectSourceCubeRefs)

  if (refs.length === 0) {
    return null
  }

  const anyError = refs.some((r) => r.status === 'error')
  if (anyError) {
    return (
      <div role="alert" className="alert alert-error mb-4">
        <ExclamationTriangleIcon className="w-5 h-5" />
        <span>
          Failed to load visualization data for this {scopeKind ?? 'scope'}. Try reloading; if it
          persists the cube may need to be recomputed.
        </span>
      </div>
    )
  }

  // Only judge "empty" once every source's cube has actually resolved to `ready`.
  const allReady = refs.every((r) => r.status === 'ready')
  if (!allReady) {
    return null
  }

  const anyWithRows = refs.some((r) => (getCubeTable(r.scopeId, r.blastType, r.spin)?.numRows ?? 0) > 0)
  if (anyWithRows) {
    return null
  }

  return (
    <div role="status" className="alert alert-info mb-4">
      <InformationCircleIcon className="w-5 h-5" />
      <div>
        <div className="font-semibold">No precomputed visualization data yet</div>
        <div className="text-sm opacity-80">
          This {scopeKind ?? 'scope'} has samples but no derived data to visualize. If the order was
          processed recently, the data pipeline may still be running — reload in a few minutes. New
          projects are only computed after they leave draft mode and have samples added.
        </div>
      </div>
    </div>
  )
}
